import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Button,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  ArrowBack,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import ReportePagosCuotasModal from "./ReportePagosCuotasModal";
import { getAuthData } from "../../utils/auth";

const ReportePagosCuotas = () => {
  const [contratos, setContratos] = useState([]);
  const [expandedContrato, setExpandedContrato] = useState(null);
  const [cuotas, setCuotas] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [cuotaSeleccionada, setCuotaSeleccionada] = useState(null);
  const [contratoSeleccionado, setContratoSeleccionado] = useState(null);
  const [loadingComprobante, setLoadingComprobante] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const authData = getAuthData();
  const token = authData?.token;

  // Corregido para manejar estados nulos o indefinidos
  const getColor = (estado) => {
    const est = estado?.toLowerCase() || "pendiente";
    if (est === "pagado") return "#4caf50";
    if (est === "vencido") return "#f44336";
    return "#ffeb3b";
  };

  useEffect(() => {
    fetchContratos();
  }, [apiUrl]);

  const fetchContratos = async () => {
    try {
      const res = await axios.get(`${apiUrl}/api/v1/cuotas/contratos-financiados`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContratos(res.data);
    } catch (err) {
      setError("Error al cargar contratos");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleContrato = async (numeroContrato) => {
    if (expandedContrato === numeroContrato) {
      setExpandedContrato(null);
    } else {
      setExpandedContrato(numeroContrato);
      const contrato = contratos.find((c) => c.numero_contrato === numeroContrato);
      setContratoSeleccionado(contrato);

      if (!cuotas[numeroContrato]) {
        try {
          const res = await axios.get(`${apiUrl}/api/v1/cuotas/cuotas/${numeroContrato}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setCuotas((prev) => ({ ...prev, [numeroContrato]: res.data }));
        } catch (err) {
          setCuotas((prev) => ({ ...prev, [numeroContrato]: [] }));
        }
      }
    }
  };

  const calcularResumen = (cuotasList) => {
    if (!cuotasList || cuotasList.length === 0) return { totalPendiente: "0.00" };
    const totalPendiente = cuotasList
      .filter((c) => c.estado !== "pagado") // Usamos .estado directamente
      .reduce((acc, c) => acc + Number(c.monto_por_cuota || 0), 0)
      .toFixed(2);
    return { totalPendiente };
  };

  const abrirModalPago = (cuota) => {
    setCuotaSeleccionada(cuota);
    setModalOpen(true);
  };

  const handlePagoRegistrado = async (numeroContrato) => {
    const res = await axios.get(`${apiUrl}/api/v1/cuotas/cuotas/${numeroContrato}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setCuotas((prev) => ({ ...prev, [numeroContrato]: res.data }));
    setModalOpen(false);
  };

  const handleVerComprobante = async (pathComprobante, cuotaId) => {
    try {
      setLoadingComprobante(cuotaId);
      // Extraemos el nombre del archivo soportando rutas de Windows o Linux
      const nombreArchivo = pathComprobante.split(/[\\/]/).pop();

      const response = await axios.get(
        `${apiUrl}/api/v1/cuotas/comprobante/${nombreArchivo}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const objectUrl = URL.createObjectURL(response.data);
      window.open(objectUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      console.error("Error al obtener el comprobante:", error);
      toast.error("No se pudo cargar el comprobante.");
    } finally {
      setLoadingComprobante(null);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ p: 3, backgroundColor: "#ffffff", borderRadius: 2, boxShadow: 3 }}>
      <Typography variant="h6" sx={{ fontFamily: "Bakbak One", color: "#0A4575", mb: 2 }}>
        CUOTAS DE CONTRATOS FINANCIADOS
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "#0A4575" }}>
            <TableRow>
              <TableCell sx={{ color: "#fff" }}>Nro Contrato</TableCell>
              <TableCell sx={{ color: "#fff" }}>Cliente</TableCell>
              <TableCell sx={{ color: "#fff" }}>Fecha</TableCell>
              <TableCell sx={{ color: "#fff" }}>Tipo de Servicio</TableCell>
              <TableCell sx={{ color: "#fff" }}>Monto Total</TableCell>
              <TableCell sx={{ color: "#fff" }}>Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contratos.map((contrato) => (
              <React.Fragment key={contrato.id}>
                <TableRow>
                  <TableCell>{contrato.numero_contrato}</TableCell>
                  <TableCell>{contrato.cliente || "Sin cliente"}</TableCell>
                  <TableCell>{contrato.fecha_creacion ? format(new Date(contrato.fecha_creacion), "dd/MM/yyyy") : "---"}</TableCell>
                  <TableCell>{contrato.tipo_servicio}</TableCell>
                  <TableCell>${contrato.precio_plan?.toFixed(2)}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleToggleContrato(contrato.numero_contrato)}>
                      {expandedContrato === contrato.numero_contrato ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0 }}>
                    <Collapse in={expandedContrato === contrato.numero_contrato} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, backgroundColor: "#f9f9f9" }}>
                        <Typography variant="h6" sx={{ fontFamily: "Bakbak One", color: "#0A4575", mb: 2 }}>Detalles de Cuotas</Typography>
                        {cuotas[contrato.numero_contrato] ? (
                          <>
                            <Table size="small">
                              <TableHead sx={{ backgroundColor: "#0A4575" }}>
                                <TableRow>
                                  <TableCell sx={{ color: "#fff" }}>Cuota #</TableCell>
                                  <TableCell sx={{ color: "#fff" }}>Vencimiento</TableCell>
                                  <TableCell sx={{ color: "#fff" }}>Monto</TableCell>
                                  <TableCell sx={{ color: "#fff" }}>Método de Pago</TableCell>
                                  <TableCell sx={{ color: "#fff" }}>Estado</TableCell>
                                  <TableCell sx={{ color: "#fff" }}>Acción</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {cuotas[contrato.numero_contrato].map((cuota) => (
                                  <TableRow key={cuota.id}>
                                    <TableCell>{cuota.no_cuota}</TableCell>
                                    <TableCell>{cuota.fecha_vencimiento ? format(new Date(cuota.fecha_vencimiento), "dd/MM/yyyy") : "---"}</TableCell>
                                    <TableCell>{parseFloat(cuota.monto_por_cuota || 0).toFixed(2)}</TableCell>
                                    <TableCell>{cuota.metodo_pago || "N/A"}</TableCell>
                                    <TableCell>
                                      <Box sx={{ bgcolor: getColor(cuota.estado), px: 1, borderRadius: 1, textAlign: "center", color: "#000" }}>
                                        {cuota.estado?.toUpperCase() || "PENDIENTE"}
                                      </Box>
                                    </TableCell>
                                    <TableCell>
                                      {cuota.estado !== "pagado" ? (
                                        <Button
                                          variant="contained"
                                          size="small"
                                          onClick={() => abrirModalPago(cuota)}
                                          sx={{ bgcolor: "#0A4575" }}
                                        >
                                          Pagar
                                        </Button>
                                      ) : (
                                        cuota.comprobante && (
                                          <IconButton
                                            color="primary"
                                            onClick={() => handleVerComprobante(cuota.comprobante, cuota.id)}
                                            disabled={loadingComprobante === cuota.id}
                                            title="Ver Comprobante"
                                          >
                                            {loadingComprobante === cuota.id ? (
                                              <CircularProgress size={24} />
                                            ) : (
                                              <VisibilityIcon />
                                            )}
                                          </IconButton>
                                        )
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            <Box sx={{ mt: 2, textAlign: "right" }}>
                              <Typography><strong>Pendiente Total:</strong> <b>${calcularResumen(cuotas[contrato.numero_contrato]).totalPendiente}</b></Typography>
                            </Box>
                          </>
                        ) : <CircularProgress size={20} />}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {modalOpen && (
        <ReportePagosCuotasModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          cuota={cuotaSeleccionada}
          contrato={contratoSeleccionado}
          onPagoRegistrado={handlePagoRegistrado}
        />
      )}

      <Button variant="contained" component={Link} to="/ventas" startIcon={<ArrowBack />} sx={{ mt: 2, bgcolor: "#0A4575" }}>
        Volver
      </Button>
    </Box>
  );
};

export default ReportePagosCuotas;